import React, { FC } from 'react';
import { FormikErrors, FormikTouched } from 'formik';

import ButtonSelectShow from 'components/button/buttonSelectShow/ButtonSelectShow';

import { normalizeShowModes } from './Event.helpers';
import { EventShow, ShowPlaybackMode } from './Event.types';
import EventShowItem from './EventShowItem';

import './EventShowsField.scss';

interface EventShowsFieldProps {
  shows: EventShow[];
  disabled?: boolean;
  pendingApproval?: boolean;
  errors?: string | string[] | FormikErrors<EventShow>[];
  touched?: FormikTouched<EventShow>[];
  // Array-level errors have no touch target; only show after submit.
  submitCount?: number;
  onChange: (shows: EventShow[]) => void;
  onSelectShow: () => void;
}

const EventShowsField: FC<EventShowsFieldProps> = ({
  shows,
  disabled = false,
  pendingApproval = false,
  errors,
  touched,
  submitCount = 0,
  onChange,
  onSelectShow,
}) => {
  const listError =
    submitCount > 0 && typeof errors === 'string' ? errors : undefined;
  const showErrors = Array.isArray(errors) ? errors : [];

  const errorsForShow = (index: number) => {
    const showError = showErrors[index];
    const showTouched = touched && touched[index];

    if (!showError || typeof showError === 'string') {
      return undefined;
    }

    return {
      time: showTouched?.time ? showError.time : undefined,
      repetition: showTouched?.repetition ? showError.repetition : undefined,
    };
  };

  const updateShow = (index: number, changes: Partial<EventShow>) => {
    const updatedShows = shows.map((show, i) =>
      i === index ? { ...show, ...changes } : show,
    );

    onChange(
      changes.playbackMode !== undefined
        ? normalizeShowModes(updatedShows)
        : updatedShows,
    );
  };

  const removeShow = (index: number) => {
    onChange(normalizeShowModes(shows.filter((_, i) => i !== index)));
  };

  return (
    <div className="event-show-list">
      {shows.map((show, index) => (
        <EventShowItem
          key={show.arrangementId}
          index={index}
          show={show}
          showCount={shows.length}
          disabled={disabled}
          pendingApproval={pendingApproval}
          errors={errorsForShow(index)}
          onRemove={() => removeShow(index)}
          onTimeChange={time => updateShow(index, { time })}
          onRepetitionChange={repetition => updateShow(index, { repetition })}
          onPlaybackModeChange={(playbackMode: ShowPlaybackMode) =>
            updateShow(index, { playbackMode })
          }
        />
      ))}

      <ButtonSelectShow
        onClick={onSelectShow}
        error={listError}
        disabled={disabled}
      />
    </div>
  );
};

export default EventShowsField;
